// risk.ts
export type RiskLevel = 0 | 1 | 2 | 3 | 4 | 5;
export interface IRiskScore { level: RiskLevel; reasons: string[] }
export interface ISafetyRisk { level: RiskLevel; isFlag: boolean; reasons: string[] }

/** Risk level → severity bucket. Single source for the colour thresholds shared
 *  by every risk indicator (Daily List badges, Progress Tracker header badge). */
export type RiskSeverity = 'none' | 'low' | 'mid' | 'high';
export function riskSeverity(level: number): RiskSeverity {
  if (level >= 4) return 'high';
  if (level === 3) return 'mid';
  if (level >= 1) return 'low';
  return 'none';
}

const HOT_WORKS = /\b(weld|welding|cut|cutting|grind|grinding|torch|burn|溶接|切断|研磨)\b/i;
const AT_HEIGHT = /\b(crane|scaffold|roof|height|elevation|aloft|hoist|クレーン|足場|屋根|高所)\b/i;

export function classifyHazard(title: string): 'hot-works' | 'working-at-height' | null {
  if (HOT_WORKS.test(title)) return 'hot-works';
  if (AT_HEIGHT.test(title)) return 'working-at-height';
  return null;
}

const cap = (n: number): RiskLevel => Math.max(0, Math.min(5, n)) as RiskLevel;
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

export function onTimeRiskForActivity(args: {
  start: string; end: string; progressPercent: number;
  status: string; today: string; isCritical: boolean; hasDeps?: boolean;
}): IRiskScore {
  const { start, end, progressPercent, status, today, isCritical, hasDeps } = args;
  // Finished work carries no on-time risk: a task marked `done` (the plan's
  // human decision) or an activity Trusstor reports `complete`.
  if (status === 'done' || status === 'complete' || progressPercent >= 100) {
    return { level: 0, reasons: [] };
  }
  // "Linked" = the task is wired into the schedule network — it depends on another
  // task and/or another task depends on it. We surface this as a reason whenever it
  // holds. Critical-path membership (isCritical) is a stricter, score-bumping signal
  // and always implies a link, so it keeps surfacing the reason on its own.
  const linked = isCritical || !!hasDeps;
  if (Date.parse(today) > Date.parse(end)) {
    // Overdue is always max risk, but still call out the dependency link so an
    // overdue task that blocks (or waits on) others reads "past its end date +1".
    return { level: 5, reasons: linked ? ['overdue', 'critical-path'] : ['overdue'] };
  }
  const span = Math.max(1, daysBetween(start, end));
  const elapsed = Math.max(0, Math.min(1, daysBetween(start, today) / span));
  const gap = elapsed - progressPercent / 100;
  // gap <= 0 means on pace / ahead / not-yet-started → NO on-time risk (0/5).
  // Only genuine slippage (gap > 0) earns a 1–5 score, so a number on the card
  // always signals a real reason. 5 is shared with the overdue early-return above.
  let level: number;
  if (gap <= 0) level = 0;
  else if (gap <= 0.15) level = 1;
  else if (gap <= 0.3) level = 2;
  else if (gap <= 0.5) level = 3;
  else if (gap <= 0.7) level = 4;
  else level = 5;
  // "behind-schedule" is the only base on-time reason. Being on pace is the
  // *absence* of risk, not a reason, so an on-pace task carries no base reason —
  // it only ever scores via the critical-path bump below.
  const reasons: string[] = gap > 0 ? ['behind-schedule'] : [];
  // Critical-path membership bumps the score by one — its slip cascades to the
  // project finish date. (The dependency *reason* below is broader than this.)
  if (isCritical && level < 5) level += 1;
  // Surface the dependency link only when there's a non-zero score to explain.
  // At level 0 the card hides the reason chip anyway, and a stray reason would
  // otherwise leak into the badge's `title` tooltip, contradicting the "0/5".
  if (linked && level > 0) {
    // With no base reason (on pace) the dependency link is the only notable
    // thing, so it leads; otherwise "behind schedule" is the bigger story and
    // stays first.
    if (reasons.length === 0) reasons.unshift('critical-path');
    else reasons.push('critical-path');
  }
  return { level: cap(level), reasons };
}

export function onTimeRiskForIssue(args: {
  dueDate?: string | null; status: string; today: string; isCritical?: boolean;
}): IRiskScore {
  const { dueDate, status, today, isCritical } = args;
  if (!dueDate || status === 'completed') return { level: 0, reasons: [] };
  const dleft = daysBetween(today, dueDate);

  // Deadline band — unchanged. Imminent = due in 2 days, 1 day, or today.
  let level: number;
  let reasons: string[];
  if (dleft < 0) { level = 5; reasons = ['overdue']; }
  else if (dleft <= 2) { level = 3; reasons = ['due-soon']; }
  else { level = 0; reasons = []; } // not imminent → no on-time risk (no reason)

  // Critical priority adds +1 and a secondary reason, ranked AFTER the deadline
  // (the deadline is the bigger driver), so the card shows e.g. "Due soon +1".
  // When the deadline carries no risk (not imminent), critical is the only reason.
  if (isCritical) {
    const deadlineAtRisk = level > 0;
    level = cap(level + 1);
    reasons = deadlineAtRisk ? [...reasons, 'critical'] : ['critical'];
  }
  return { level: cap(level), reasons };
}

export function safetyRiskForActivity(args: { title: string }): ISafetyRisk {
  const hazard = classifyHazard(args.title);
  if (!hazard) return { level: 0, isFlag: false, reasons: [] };
  return {
    level: 3,
    isFlag: true,
    reasons: [hazard === 'hot-works' ? 'hazard-hot-works' : 'hazard-at-height'],
  };
}

export function safetyRiskForIssue(args: { isSafety: boolean }): ISafetyRisk {
  // Critical no longer inflates safety — it now feeds on-time risk instead.
  if (!args.isSafety) return { level: 0, isFlag: false, reasons: [] };
  return { level: 3, isFlag: true, reasons: ['safety-issue'] };
}
